import { ListItem } from "@shared/components/Data/List";
import {
    buttonIconVariants,
    Text,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger
} from "@solomon-labs/design/components/ui";
import React from "react";

type TransactionRowSize = "sm" | "md";

const TEXT_VARIANT_BY_SIZE: Record<TransactionRowSize, "small" | "body"> = {
    sm: "small",
    md: "body"
};

// Hover-tooltip open delay for the destination line. Long enough that
// it doesn't fire on incidental cursor passes (the row itself is a
// link out to Solscan, so hovers are frequent), short enough that an
// intentional hover surfaces the full address quickly.
const DESTINATION_TOOLTIP_DELAY_MS = 400;

function shortenAddress(addr: string): string {
    return `${addr.slice(0, 4)}..${addr.slice(-4)}`;
}

interface TransactionRowProps {
    /** Stable key for list reconciliation. */
    itemKey: string;
    /** Outbound link target — typically a Solscan tx URL. Omit for
     *  rows with no on-chain transaction (renders as a plain row). */
    href?: string;
    /** Leading pill icon — either an image `src` string (rendered as
     *  `<img>`) or a rendered icon element (e.g. `<EditIcon … />`, which
     *  follows `currentColor`). */
    icon: string | React.ReactNode;
    /** Optional trailing click-out affordance — a rendered icon element
     *  (e.g. `<ArrowShortUpRightIcon />`) shown at the row's end with an
     *  `ml-2` gap and the transparent ButtonIcon hover treatment
     *  (charcoal → ember on hover). Signals the row opens externally. */
    trailingIcon?: React.ReactNode;
    /** Sizing/utility classes for the icon `<img>` inside the pill.
     *  Defaults to `size-3` (12px) — right for the compact action
     *  glyphs (plus/minus/stake). Branded marks with finer strokes
     *  (e.g. the Solomon logomark on distributions) need a larger
     *  footprint to read at all — pass e.g. `size-5`. */
    iconClassName?: string;
    /** When true, drop the circular pill background + `overflow-hidden`
     *  clip and render the icon bare (still occupying the `size-6` slot
     *  so rows stay aligned). Use for a standalone branded mark like the
     *  Solomon logomark — its square frame would otherwise have its
     *  corners clipped by the round pill. */
    iconBare?: boolean;
    /** Background + shape utilities for the icon pill. Defaults to the
     *  neutral round `bg-line-base rounded-full`. Override the whole
     *  treatment — e.g. `bg-surface-raised rounded-none` for a square
     *  chip. Ignored when `iconBare`. */
    iconPillClassName?: string;
    /** Full label shown on `md+` (e.g. "Buy USDv", "Distribution"). */
    label: string;
    /** Short label shown on narrow viewports (e.g. "Buy", "Yield"). */
    labelShort: string;
    /** First amount line — a string (e.g. "+$100.00") or a rendered
     *  node (e.g. "Claim → Stream" with an inline arrow). Always
     *  rendered. */
    primary: React.ReactNode;
    /** Optional second amount line in `text-foreground-imprint` (e.g.
     *  "− 100.00 USDC", "on $12,572"). */
    secondary?: string;
    /** Optional human-readable label for the destination wallet
     *  (e.g. "Source Account"). When set, renders verbatim under
     *  the row label as "Destination: {destinationLabel}". When
     *  unset but `destinationAddress` is set, the shortened address
     *  surfaces instead (e.g. "E9Yt..Wi7x"). */
    destinationLabel?: string;
    /** Optional destination wallet address. Drives the shortened-
     *  address fallback (when no `destinationLabel`) and the tooltip
     *  body (full address). Required if the row is meant to surface
     *  a destination at all — set it on every row that should show
     *  a destination line, regardless of whether you also pass
     *  `destinationLabel`. */
    destinationAddress?: string;
    /** Full date shown on `md+` (e.g. "Apr 22, 2026"). */
    date: string;
    /** Short date shown on narrow viewports (e.g. "Apr 22"). */
    dateShort: string;
    /** When true, the column order is `date · action · amount` — the
     *  date moves to a fixed-width leading column and the amount right-
     *  aligns at the row's end. Default is `action · amount · date`. */
    dateLeading?: boolean;
    /** Typography size. `md` (body) is the default. `sm` (small) is
     *  denser — used inside the active-state action panel
     *  where horizontal width is constrained. */
    size?: TransactionRowSize;
    /** Override for the inter-row divider color, forwarded to the
     *  underlying `ListItem`. Defaults to the `line-neutral` token
     *  (`ListItem`'s own default — a soft olive-tinted line). Pass a
     *  stronger token like `rgb(var(--color-line-base))` when the
     *  list needs visually present dividers. */
    borderColor?: string;
}

/**
 * Presentational row used by both `TransactionHistory` (live wallet
 * transactions) and `AccountTransactionHistory` (weekly yield
 * distributions). Layout: leading icon pill, label column (with
 * optional destination line beneath), amount column (1 or 2 lines),
 * and a right-aligned date column.
 *
 * The `size` prop swaps the underlying `<Text>` variant — `md` →
 * `body`, `sm` → `small` — for the four text slots. The icon pill
 * keeps its `size-6` footprint either way; the difference is purely
 * typographic density.
 */
export function TransactionRow({
    itemKey,
    href,
    icon,
    trailingIcon,
    iconClassName = "size-3",
    iconBare = false,
    iconPillClassName = "bg-line-base rounded-full",
    label,
    labelShort,
    primary,
    secondary,
    destinationLabel,
    destinationAddress,
    date,
    dateShort,
    dateLeading = false,
    size = "md",
    borderColor
}: TransactionRowProps): React.ReactElement {
    const variant = TEXT_VARIANT_BY_SIZE[size];
    const destinationDisplay =
        destinationLabel ??
        (destinationAddress ? shortenAddress(destinationAddress) : null);
    // Rows with no destination line and no secondary amount collapse to
    // a single line — center them so the icon, label, amount, and date
    // sit on a shared baseline (2-line rows stay top-aligned).
    const isSingleLine = !secondary && !destinationDisplay;

    const iconEl = (
        <div
            className={`size-6 flex items-center justify-center shrink-0 relative z-10${
                iconBare ? "" : ` ${iconPillClassName} overflow-hidden`
            }`}
        >
            {typeof icon === "string" ? (
                <img src={icon} alt="" className={iconClassName} />
            ) : (
                icon
            )}
        </div>
    );

    const labelCol = (
        <div className="flex-1 relative z-10 ml-3 flex flex-col gap-1 min-w-0">
            <Text
                variant={variant}
                render={<span />}
                className="text-foreground-base"
            >
                <span className="md:hidden">{labelShort}</span>
                <span className="hidden md:inline">{label}</span>
            </Text>
            {destinationDisplay &&
                (destinationAddress ? (
                    <TooltipProvider delay={DESTINATION_TOOLTIP_DELAY_MS}>
                        <Tooltip>
                            <TooltipTrigger
                                render={
                                    <Text
                                        variant={variant}
                                        render={<span />}
                                        className="text-foreground-imprint truncate"
                                    />
                                }
                            >
                                Destination: {destinationDisplay}
                            </TooltipTrigger>
                            <TooltipContent side="top">
                                {destinationAddress}
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                ) : (
                    <Text
                        variant={variant}
                        render={<span />}
                        className="text-foreground-imprint truncate"
                    >
                        Destination: {destinationDisplay}
                    </Text>
                ))}
        </div>
    );

    const amountCol = (
        <div className="flex flex-col gap-1 shrink-0 text-right relative z-10">
            <Text
                variant={variant}
                render={<span />}
                className="text-foreground-base whitespace-nowrap"
            >
                {primary}
            </Text>
            {secondary && (
                <Text
                    variant={variant}
                    render={<span />}
                    className="text-foreground-imprint whitespace-nowrap"
                >
                    {secondary}
                </Text>
            )}
        </div>
    );

    const dateEl = (
        <Text
            variant={variant}
            render={<span />}
            className={
                dateLeading
                    ? "text-foreground-base shrink-0 w-14 relative z-10"
                    : "text-foreground-base text-right flex-1 relative z-10"
            }
        >
            <span className="md:hidden">{dateShort}</span>
            <span className="hidden md:inline">{date}</span>
        </Text>
    );

    const trailingEl = trailingIcon ? (
        <span
            className={`${buttonIconVariants({
                variant: "transparent",
                size: "md"
            })} shrink-0 relative z-10 ml-2`}
        >
            {trailingIcon}
        </span>
    ) : null;

    const row = (
        <ListItem
            itemKey={itemKey}
            href={href}
            external
            align={isSingleLine ? "center" : "start"}
            borderColor={borderColor}
        >
            {/* When `dateLeading`: icon + date grouped at `gap-2` (tighter
                than the row's `gap-3`) · action · amount. Otherwise:
                icon · action · amount · date. Trailing click-out
                affordance closes the row. */}
            {dateLeading ? (
                <div className="flex items-center gap-2 shrink-0 relative z-10">
                    {iconEl}
                    {dateEl}
                </div>
            ) : (
                iconEl
            )}
            {labelCol}
            {amountCol}
            {!dateLeading && dateEl}
            {trailingEl}
        </ListItem>
    );

    return row;
}
